import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  buildMessengerV2ConversationPath,
  isMessengerV2InboxPath,
  resolveMessengerV2RouteConversationId,
  shouldShowMessengerV2Inbox,
} from './messengerV2Routes.js';
import { useMessengerV2Controller } from './useMessengerV2Controller.js';
import {
  closeMessengerV2ConversationNotifications,
  resolveMessengerV2NotificationVisibility,
} from '../../lib/messengerDesktopNotifications.js';

const MessengerV2ControllerContext = createContext(null);

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

export const MessengerV2ControllerProvider = ({ children }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const routeConversationId = resolveMessengerV2RouteConversationId(location.pathname);
  const [surfaceConversationId, setSurfaceConversationId] = useState(null);

  const handleOpenConversation = useCallback(
    (conversationId, conversationMeta = {}) => {
      const normalizedConversationId = toStringOrNull(conversationId);
      if (!normalizedConversationId) return;
      if (
        surfaceConversationId &&
        normalizedConversationId === toStringOrNull(surfaceConversationId)
      ) {
        return;
      }
      const nextPath = buildMessengerV2ConversationPath(conversationId, conversationMeta);
      if (location.pathname === nextPath || location.pathname.startsWith(`${nextPath}/`)) {
        return;
      }
      navigate(nextPath);
    },
    [location.pathname, navigate, surfaceConversationId],
  );

  const controller = useMessengerV2Controller({
    formatMessage: intl.formatMessage,
    initialConversationId: routeConversationId,
    onOpenConversation: handleOpenConversation,
    readReceiptConversationId: routeConversationId || surfaceConversationId,
  });
  const selectConversation = controller.selectConversation;

  useEffect(() => {
    if (!routeConversationId || controller.state?.lifecycle?.hydrationReady !== true) return;

    const routeConversation = (controller.state?.conversations ?? []).find(
      (conversation) => toStringOrNull(conversation?.id ?? null) === routeConversationId,
    );
    if (String(routeConversation?.kind || '').toLowerCase() !== 'room') return;

    const roomPath = buildMessengerV2ConversationPath(routeConversationId, routeConversation);
    if (roomPath === '/messages' || location.pathname === roomPath) return;

    navigate(roomPath, { replace: true });
  }, [
    controller.state?.conversations,
    controller.state?.lifecycle?.hydrationReady,
    location.pathname,
    navigate,
    routeConversationId,
  ]);

  useEffect(() => {
    if (!routeConversationId) return undefined;

    void closeMessengerV2ConversationNotifications({ conversationId: routeConversationId });

    const closeActiveConversationNotifications = () => {
      if (resolveMessengerV2NotificationVisibility() !== 'visible') return;
      void closeMessengerV2ConversationNotifications({ conversationId: routeConversationId });
    };

    window.addEventListener('focus', closeActiveConversationNotifications);
    document.addEventListener('visibilitychange', closeActiveConversationNotifications);

    return () => {
      window.removeEventListener('focus', closeActiveConversationNotifications);
      document.removeEventListener('visibilitychange', closeActiveConversationNotifications);
    };
  }, [routeConversationId]);

  useEffect(() => {
    if (
      !isMessengerV2InboxPath(location.pathname) ||
      controller.state?.lifecycle?.hydrationReady !== true
    ) {
      return;
    }

    const activeConversationId = toStringOrNull(controller.state?.activeConversationId ?? null);
    if (!activeConversationId) return;

    const activeConversation = (controller.state?.conversations ?? []).find(
      (conversation) => toStringOrNull(conversation?.id ?? null) === activeConversationId,
    );
    if (!activeConversation) return;

    if (String(activeConversation?.kind || '').toLowerCase() === 'room') {
      selectConversation?.(null, { notifyRoute: false });
      return;
    }

    if (shouldShowMessengerV2Inbox(location.state)) return;

    navigate(buildMessengerV2ConversationPath(activeConversationId, activeConversation), {
      replace: true,
    });
  }, [
    controller.state?.activeConversationId,
    controller.state?.conversations,
    controller.state?.lifecycle?.hydrationReady,
    selectConversation,
    location.pathname,
    location.state,
    navigate,
  ]);

  return (
    <MessengerV2ControllerContext.Provider value={{ ...controller, setSurfaceConversationId }}>
      {children}
    </MessengerV2ControllerContext.Provider>
  );
};

export const useMessengerV2ControllerContext = () => useContext(MessengerV2ControllerContext);

export default MessengerV2ControllerContext;
